<!--
    Deliberately renders no UI. This component drives a spa connection through the
    shared useSpaClient composable and publishes expected-vs-actual live state
    through its `health` event; the VesselHealthChecks card renders the result,
    including the mismatch explanations that used to appear here as page-level banners.
-->
<template>
    <span class="spa-state-validator" hidden />
</template>

<script setup lang="ts">
import { useSpaClient } from '@/lib/useSpaClient'
import {
    PresetScheduleType,
    TransporterConnectionStatus,
    type Schedule,
    type FlowZone,
    type TemperatureControlZone
} from '@geckoal/vessel-client'
import {
    FlowZoneInitiator,
    GatewayConnectionStatus,
    OperationMode,
    RfStrength
} from '@geckoal/web-models'
import {
    ActivityStatusV1IdEnum,
    type GetAccountVesselSettingsSchemaV1,
    type GetUpcomingVesselActivityV1200Response
} from '@geckoal/gecko-api-client'
import { computed, onMounted, onUnmounted, ref, watch, watchEffect } from 'vue'
import { toZonedTime } from 'date-fns-tz'
import { rfStrengthLabel, rfStrengthSeverity, type SpaHealthCheck, type SpaHealthChecks } from '@/lib/vesselHealth'

const props = defineProps<{
    accountId: number
    monitorId: string
    vesselSettings?: GetAccountVesselSettingsSchemaV1
    upcomingActivity?: GetUpcomingVesselActivityV1200Response | null
    vesselTimezone?: string
}>()

const emit = defineEmits<{
    /** Expected vs. actual live spa values, consumed by the Health Checks card. */
    health: [SpaHealthChecks]
}>()

// Shared spa connection (iot client by default, legacy vessel client when toggled).
const spa = useSpaClient()
const { spaState, spaConfig, presets, lastKnownInitiators, cloudConnectionStatus } = spa

// Connection state.
// `isConnected` tracks TRANSPORT-level readiness (the cloud transport is up), matching
// the component's previous event-driven ref that flipped true on TransporterEvent.CONNECTED
// and false on DISCONNECTED, NOT gateway-level (spa.connected) or vessel-level
// (spa.spaConnected) status. This keeps the connect() re-entry guard and the health-check
// gating behaving exactly as before: we consider ourselves connected as soon as the
// transport is up, independent of whether the gateway/vessel has reported in yet. The
// composable surfaces this reactively via `cloudConnectionStatus`; `spa.isConnected()` is
// the same notion but imperative, so it would not drive the reactive `healthChecks` emit.
const isConnecting = ref(false)
const isConnected = computed(() => cloudConnectionStatus.value === TransporterConnectionStatus.CONNECTED)
const connectionError = ref<string | undefined>()

// Generation counter to prevent stale async callbacks after unmount or reconnect
let connectGeneration = 0

// --- Helpers ---

const operationModeName = (mode: OperationMode): string => {
    switch (mode) {
        case OperationMode.AWAY: return 'Away'
        case OperationMode.STANDARD: return 'Standard'
        case OperationMode.SAVINGS: return 'Savings'
        case OperationMode.SUPER_SAVINGS: return 'Super Savings'
        case OperationMode.WEEKENDER: return 'Weekender'
        case OperationMode.OTHER: return 'Other'
        default: return `Unknown (${mode})`
    }
}

// --- Schedule Checking ---

/**
 * Check if the current time falls within any schedule window for a given type and operation mode.
 * Converts the current time to the spa's local timezone before comparison, since preset
 * schedules are defined in the spa's local time.
 *
 * Schedule semantics: A schedule with startDay→endDay and startHour:startMinute→endHour:endMinute
 * means "run the time window on EACH day within the day range". It is NOT a single continuous
 * block spanning from startDay at startTime through endDay at endTime. The calendar view
 * renders one block per day in the range, which is the correct interpretation.
 *
 * When the time window crosses midnight (endTime <= startTime), the window on each day
 * extends from startTime to midnight, then from midnight to endTime the following morning.
 */
function isInScheduleWindow(schedules: Schedule[], type: PresetScheduleType, now: Date, timezone?: string): boolean {
    // Convert current time to the spa's timezone. If no timezone is available, fall back to browser local time.
    const spaLocal = timezone ? toZonedTime(now, timezone) : now
    const currentDay = spaLocal.getDay() // 0=Sunday, 6=Saturday
    const currentMinutes = spaLocal.getHours() * 60 + spaLocal.getMinutes()

    return schedules
        .filter(s => s.type === type)
        .some(schedule => {
            const startMinutes = schedule.startHour * 60 + schedule.startMinute
            const endMinutes = schedule.endHour * 60 + schedule.endMinute
            const crossesMidnight = endMinutes <= startMinutes

            // First, determine if today falls within the schedule's day range.
            const isDayInRange = isDayWithinRange(currentDay, schedule.startDay, schedule.endDay)

            if (crossesMidnight) {
                // The daily time window crosses midnight, e.g. 23:00–02:00.
                // This means:
                //   - On each day in the range: active from startMinutes to midnight (23:00–00:00)
                //   - On the NEXT day after each day in range: active from midnight to endMinutes (00:00–02:00)

                // Check if we're in the evening portion (startMinutes → midnight) on a day in range
                if (isDayInRange && currentMinutes >= startMinutes) return true

                // Check if we're in the morning portion (midnight → endMinutes) because
                // yesterday was in the day range
                const yesterday = (currentDay + 6) % 7 // (currentDay - 1 + 7) % 7
                const wasYesterdayInRange = isDayWithinRange(yesterday, schedule.startDay, schedule.endDay)
                if (wasYesterdayInRange && currentMinutes < endMinutes) return true
            } else {
                // The daily time window does NOT cross midnight, e.g. 12:00–14:00.
                // Simply check: is today in the day range AND is the current time within the window?
                if (isDayInRange && currentMinutes >= startMinutes && currentMinutes < endMinutes) return true
            }

            return false
        })
}

/** Returns true if `day` falls within the inclusive range [rangeStart, rangeEnd], accounting for week wrapping. */
function isDayWithinRange(day: number, rangeStart: number, rangeEnd: number): boolean {
    if (rangeStart <= rangeEnd) {
        // Normal range, e.g. Mon(1)–Fri(5) or Sun(0)–Sat(6)
        return day >= rangeStart && day <= rangeEnd
    }
    // Wrapping range, e.g. Fri(5)–Mon(1)
    return day >= rangeStart || day <= rangeEnd
}

// --- Computed: Temperature Control Zone ---

const temperatureControlZone = computed(() => {
    const tempControlsConfig = spaConfig.value?.zones?.temperatureControl
    const tempControlZones = spaState.value?.zones?.temperatureControl
    if (tempControlsConfig === undefined || tempControlZones === undefined) return undefined
    return Object.values(tempControlsConfig)
        .map((tempCtrl: any) => tempControlZones[tempCtrl.configId as string] as TemperatureControlZone)
        .at(0)
})

// --- Computed: Validation State ---

const smartRoutinesEnabled = computed(() => {
    return props.vesselSettings?.settings?.features?.smart_routines?.enabled === true
})

const sessionInProgress = computed(() => {
    return props.upcomingActivity?.status?.id === ActivityStatusV1IdEnum.InProgress
})

const sessionPreparing = computed(() => {
    return props.upcomingActivity?.status?.id === ActivityStatusV1IdEnum.Preparing
})

const currentOperationMode = computed(() => {
    return spaState.value?.features?.operationMode
})

const isFiltrationActive = computed(() => {
    const flowZones = spaState.value?.zones?.flow
    if (!flowZones) return false
    for (const [zoneId, zone] of Object.entries(flowZones)) {
        const initiators = (zone as FlowZone)?.initiators_ ?? lastKnownInitiators.value[zoneId] ?? []
        if ((initiators as FlowZoneInitiator[]).includes(FlowZoneInitiator.FILTRATION)) {
            return true
        }
    }
    return false
})

const expectedOperationMode = computed((): OperationMode | undefined => {
    if (!smartRoutinesEnabled.value) return undefined
    if (sessionInProgress.value || sessionPreparing.value) return OperationMode.STANDARD
    return OperationMode.AWAY
})

// --- Computed: Schedule Expectations ---
// Extracted so both the validation messages and the Health Checks card can use them.

const currentModeSchedules = computed((): Schedule[] | undefined => {
    if (!presets.value || currentOperationMode.value === undefined) return undefined
    const schedules = (presets.value as Record<number, Schedule[]>)[currentOperationMode.value]
    if (!schedules || schedules.length === 0) return undefined
    return schedules
})

const shouldBeFiltrating = computed((): boolean | undefined => {
    const schedules = currentModeSchedules.value
    if (schedules === undefined) return undefined
    return isInScheduleWindow(schedules, PresetScheduleType.FILTRATION, new Date(), props.vesselTimezone)
})

const shouldBeInEcoMode = computed((): boolean | undefined => {
    const schedules = currentModeSchedules.value
    if (schedules === undefined) return undefined
    return isInScheduleWindow(schedules, PresetScheduleType.ECONOMY, new Date(), props.vesselTimezone)
})

const isEcoModeActive = computed(() => {
    return temperatureControlZone.value?.mode_?.eco === true
})

// --- Computed: Individual Validations ---
// Each validation returns the expected value, the actual value, and — when they
// disagree — the full explanation of what is wrong. Those explanations used to be
// rendered here as banners; they are now the tooltips on the Health Checks card rows.

const NO_SCHEDULES_NOTE = 'No preset schedules were found for the current operation mode, so there is nothing to compare against.'
const SETPOINT_TOLERANCE_C = 0.5

const onOff = (value: boolean | undefined, onLabel: string, offLabel: string): string | undefined => {
    if (value === undefined) return undefined
    return value ? onLabel : offLabel
}

const currentModeName = computed(() => {
    if (currentOperationMode.value === undefined) return 'unknown'
    return operationModeName(currentOperationMode.value)
})

const operationModeCheck = computed((): SpaHealthCheck => {
    const current = currentOperationMode.value !== undefined ? operationModeName(currentOperationMode.value) : undefined

    // For non-smart-routine users we do NOT validate the operation mode itself.
    // The watercare mode is entirely user-controlled (set manually by the spa owner),
    // and there is no automated expected value to compare against. The preset schedules
    // only define filtration/eco windows per mode - they don't dictate which mode should
    // be active at any given time.
    if (expectedOperationMode.value === undefined) {
        return {
            current,
            note: 'Smart routines are off, so the watercare mode is set manually by the spa owner and has no expected value.'
        }
    }

    const expected = operationModeName(expectedOperationMode.value)
    if (current === undefined || current === expected) return { expected, current }

    const reason = sessionInProgress.value || sessionPreparing.value ? 'a session is in progress/preparing' : 'no session is active'
    return { expected, current, issue: `Operation mode is "${current}" but should be "${expected}" because ${reason}.` }
})

const ecoScheduleCheck = computed((): SpaHealthCheck => {
    const current = onOff(isEcoModeActive.value, 'Active', 'Off')
    if (shouldBeInEcoMode.value === undefined) return { current, note: NO_SCHEDULES_NOTE }

    const expected = shouldBeInEcoMode.value ? 'Active' : 'Off'
    if (current === expected) return { expected, current }

    const issue = shouldBeInEcoMode.value
        ? `Eco mode should be active based on the current schedule for "${currentModeName.value}" mode, but it is not enabled.`
        : `Eco mode is currently active but is not scheduled at this time for "${currentModeName.value}" mode.`
    return { expected, current, issue }
})

const filtrationCheck = computed((): SpaHealthCheck => {
    const current = onOff(isFiltrationActive.value, 'Running', 'Off')
    if (shouldBeFiltrating.value === undefined) return { current, note: NO_SCHEDULES_NOTE }

    const expected = shouldBeFiltrating.value ? 'Running' : 'Off'
    if (current === expected) return { expected, current }

    const issue = shouldBeFiltrating.value
        ? `Filtration should be running according to the current schedule for "${currentModeName.value}" mode, but it is not active.`
        : `Filtration is currently active but is not scheduled to run at this time for "${currentModeName.value}" mode.`
    return { expected, current, issue }
})

/** Only meaningful while a smart routine session is running with a target temperature. */
const heaterSetPointCheck = computed((): SpaHealthCheck | undefined => {
    // The API exposes setPointC on the activity response (optional number field).
    // Type assertion used because the installed client version predates this field.
    const expectedSetPointC = (props.upcomingActivity as (GetUpcomingVesselActivityV1200Response & { setPointC?: number }) | null | undefined)?.setPointC
    if (!smartRoutinesEnabled.value || !sessionInProgress.value || expectedSetPointC == null) return undefined

    const actualSetPointC = temperatureControlZone.value?.setPoint
    const asF = (c: number) => ((c * 9) / 5 + 32).toFixed(1)
    const expected = `${expectedSetPointC} °C`

    if (actualSetPointC == null) return { expected, current: undefined }

    const current = `${actualSetPointC} °C`
    if (Math.abs(actualSetPointC - expectedSetPointC) <= SETPOINT_TOLERANCE_C) return { expected, current }

    return {
        expected,
        current,
        issue: `Heater setpoint is ${actualSetPointC}°C (${asF(actualSetPointC)}°F) but should be ${expectedSetPointC}°C (${asF(expectedSetPointC)}°F) for the current smart routine session.`
    }
})

/**
 * RF link quality between the spa pack and the in.touch module, reported by the spa
 * itself. This is the same value the Gecko Spa Status page shows as "Signal: Weak",
 * which is far more actionable for support than a raw WiFi RSSI number.
 */
const rfSignalCheck = computed((): SpaHealthCheck => {
    const strength = spaState.value?.features?.rf?.strength_
    const expected = `${rfStrengthLabel(RfStrength.GOOD)} or better`

    if (strength === undefined) {
        return { expected, note: 'The spa has not reported an RF signal strength.' }
    }

    const current = rfStrengthLabel(strength)
    const severity = rfStrengthSeverity(strength)

    return {
        expected,
        current,
        severity,
        note:
            severity === 'ok'
                ? undefined
                : `The RF link between the spa pack and the in.touch module is ${current.toLowerCase()}. Check the module placement, the antenna, and the RF channel.`
    }
})

/**
 * Whether the in.touch module currently holds its link to the cloud. This is the
 * module's own connectivity state from the shadow, which is authoritative — unlike
 * inferring liveness from how recently a sample happened to arrive.
 */
const moduleOnlineCheck = computed((): SpaHealthCheck => {
    const gatewayStatus = spaState.value?.connectivity_?.gatewayStatus
    const expected = 'Connected'

    if (gatewayStatus === undefined || gatewayStatus === GatewayConnectionStatus.UNKNOWN) {
        return { expected, current: 'Unknown', severity: 'unknown', note: 'The module has not reported a cloud connection state.' }
    }

    if (gatewayStatus === GatewayConnectionStatus.DISCONNECTED) {
        return {
            expected,
            current: 'Disconnected',
            issue: 'The in.touch module has lost its connection to the cloud, so the spa cannot be monitored or controlled remotely. Check the spa power and the home network.'
        }
    }

    return { expected, current: 'Connected' }
})

// --- Health Checks Payload ---
// Surfaced to the parent so the Health Checks card can show live spa values
// without opening a second MQTT connection.

const healthChecks = computed((): SpaHealthChecks => {
    const connected = isConnected.value && spaState.value !== undefined
    if (!connected) {
        return { connected, connecting: isConnecting.value, error: connectionError.value }
    }

    return {
        connected,
        connecting: isConnecting.value,
        error: connectionError.value,
        operationMode: operationModeCheck.value,
        ecoSchedule: ecoScheduleCheck.value,
        filtration: filtrationCheck.value,
        heaterSetPoint: heaterSetPointCheck.value,
        rfSignal: rfSignalCheck.value,
        moduleOnline: moduleOnlineCheck.value
    }
})

// watchEffect (rather than watch + deep) so the payload is re-emitted whenever any
// underlying signal changes — connection state, spa state, presets or settings.
watchEffect(() => emit('health', healthChecks.value))

// --- Spa Connection ---
// The connection LOGIC and lifecycle (connect/disconnect, live-state mapping, preset
// loading) are centralized in the shared useSpaClient composable, which also backs
// GeckoSpaStatus. This standardizes HOW a connection is managed and lets the active
// client (iot by default, legacy vessel when toggled in the admin portal) be selected
// transparently.
//
// NOTE: this does NOT share a single connection instance across views. `useSpaClient()`
// is a plain factory: every call allocates its own client and opens its own MQTT
// connection. So when this component and GeckoSpaStatus are mounted on the same page,
// two concurrent connections still exist, exactly as the original NOTE warned. That is
// acceptable for an admin tool; only the shared LOGIC (not a shared socket) changed.
//
// Preset schedules are only available on the vessel path; on the iot path `presets` is
// null and the schedule checks degrade to "no schedules" rather than throwing.

const connect = async () => {
    if (isConnecting.value || isConnected.value) return

    const myGeneration = ++connectGeneration
    isConnecting.value = true
    connectionError.value = undefined

    try {
        await spa.connect({ monitorId: props.monitorId, accountId: props.accountId }, { loadPresets: true })
        if (myGeneration !== connectGeneration) {
            // Component unmounted or reconnect triggered during connect - clean up
            await spa.disconnect()
            return
        }
    } catch (err: any) {
        if (myGeneration !== connectGeneration) return
        connectionError.value = err?.message ?? 'Unknown connection error'
    } finally {
        if (myGeneration === connectGeneration) {
            isConnecting.value = false
        }
    }
}

const disconnectFromSpa = async () => {
    // Invalidate any in-flight connect calls
    connectGeneration++
    await spa.disconnect()
    connectionError.value = undefined
}

// --- Lifecycle ---

onMounted(() => {
    connect()
})

onUnmounted(() => {
    disconnectFromSpa()
})

// Reconnect if monitorId changes
watch(() => props.monitorId, async (newId, oldId) => {
    if (newId !== oldId) {
        await disconnectFromSpa()
        await connect()
    }
})
</script>
